/**
 * Admin Health Dashboard (v1.4.4 §1)
 *
 * Real-time server, database, and app metrics. Admin-only. Auto-refreshes
 * every 10 s by default; toggle off to inspect a snapshot.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity, Cpu, Database, HardDrive, MemoryStick, RefreshCw, Server, Users,
  Receipt, Lock, Clock, Zap,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from 'recharts';

const REFRESH_MS = 10_000;

function StatTile({ icon: Icon, label, value, sub, tone = 'default', testid }) {
  const tones = {
    default: 'text-foreground',
    good: 'text-green-600 dark:text-green-400',
    warn: 'text-amber-600 dark:text-amber-400',
    bad: 'text-red-500 dark:text-red-400',
  };
  return (
    <div className="p-3 rounded-md border border-border bg-card" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      </div>
      <p className={`text-2xl font-bold tracking-tight font-['Outfit'] tabular-nums ${tones[tone] || tones.default}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function pctTone(pct, warnAt = 70, badAt = 90) {
  if (pct >= badAt) return 'bad';
  if (pct >= warnAt) return 'warn';
  return 'good';
}

export default function AdminHealthPage() {
  const [snap, setSnap] = useState(null);
  const [latency, setLatency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [metricsDays, setMetricsDays] = useState(7);
  const [metricsData, setMetricsData] = useState(null);

  const loadMetrics = useCallback(async (days) => {
    try {
      const { data } = await api.get(`/api/admin/health/metrics?days=${days}`);
      setMetricsData(data);
    } catch (err) {
      console.warn('[health] metrics fetch failed:', err?.message || err);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/api/admin/health/summary'),
        api.get('/api/admin/health/latency'),
      ]);
      setSnap(s.data);
      setLatency(l.data);
      setRefreshedAt(new Date());
    } catch (err) {
      console.warn('[health] fetch failed:', err?.message || err);
      toast.error('Health fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMetrics(metricsDays); }, [loadMetrics, metricsDays]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [auto, load]);

  // Format metrics samples for charts — must be before early returns (hooks rules)
  const chartSamples = useMemo(() => {
    const samples = metricsData?.samples || [];
    return samples.map((s) => ({
      ...s,
      label: new Date(s.ts).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }),
    }));
  }, [metricsData]);

  if (loading && !snap) return <p className="p-6 text-sm text-muted-foreground">Loading health snapshot…</p>;
  if (!snap) return <p className="p-6 text-sm text-muted-foreground">Health data unavailable.</p>;

  const { app, system, database, collections, app_counts, recent_login_records } = snap;
  const memPct = system?.memory?.used_pct ?? 0;
  const diskAppPct = system?.disk_app?.used_pct ?? 0;
  const cpuPct = system?.cpu?.load_pct_1m ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-health-page">
      {/* Header */}
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit'] flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            App Health
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Live server, database, and app metrics. Admin-only.</p>
        </div>
        <div className="flex items-center gap-2">
          {refreshedAt && <span className="text-xs text-muted-foreground" data-testid="health-refreshed-at">Updated {refreshedAt.toLocaleTimeString()}</span>}
          <Button variant={auto ? 'default' : 'outline'} size="sm" onClick={() => setAuto((a) => !a)} data-testid="health-toggle-auto">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${auto ? 'animate-spin' : ''}`} />
            {auto ? 'Auto 10s' : 'Manual'}
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="health-refresh-btn">Refresh now</Button>
        </div>
      </div>

      {/* App snapshot */}
      <Card data-testid="health-app-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Server className="h-4 w-4 text-primary" />App</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile icon={Server} label="Version" value={app.version} sub={app.python ? `Py ${app.python}` : ''} testid="health-app-version" />
            <StatTile icon={Clock} label="Uptime" value={app.uptime_label} sub="since last redeploy" tone="good" testid="health-app-uptime" />
            <StatTile icon={Activity} label="Generated" value={new Date(snap.generated_at).toLocaleTimeString()} sub="server time" testid="health-app-generated" />
            <StatTile icon={Activity} label="Platform" value={(app.platform || '').split('-')[0] || '—'} sub={app.platform} testid="health-app-platform" />
          </div>
        </CardContent>
      </Card>

      {/* System */}
      <Card data-testid="health-system-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" />System</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile icon={MemoryStick} label="Memory used" value={`${memPct}%`} sub={`${system?.memory?.used_mb || 0} / ${system?.memory?.total_mb || 0} MB used · ${system?.memory?.available_mb || 0} MB free`} tone={pctTone(memPct)} testid="health-mem" />
            <StatTile icon={Cpu} label="CPU load (1m)" value={system?.cpu?.load_1m ?? '—'} sub={`${cpuPct.toFixed?.(1) || cpuPct}% of ${system?.cpu?.cpu_count || '?'} cores`} tone={pctTone(cpuPct)} testid="health-cpu" />
            <StatTile icon={HardDrive} label="Disk used (/app)" value={`${diskAppPct}%`} sub={`${system?.disk_app?.used_gb || 0} GB used · ${system?.disk_app?.free_gb || 0} GB free · ${system?.disk_app?.total_gb || 0} GB total`} tone={pctTone(diskAppPct)} testid="health-disk-app" />
            <StatTile icon={HardDrive} label="Disk used (/)" value={`${system?.disk_root?.used_pct || 0}%`} sub={`${system?.disk_root?.used_gb || 0} GB used · ${system?.disk_root?.free_gb || 0} GB free · ${system?.disk_root?.total_gb || 0} GB total`} tone={pctTone(system?.disk_root?.used_pct || 0)} testid="health-disk-root" />
          </div>
        </CardContent>
      </Card>

      {/* Database */}
      <Card data-testid="health-db-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Database className="h-4 w-4 text-primary" />MongoDB · {database?.name}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatTile icon={Database} label="Collections" value={database?.collections ?? '—'} sub="logical groups" testid="health-db-collections" />
            <StatTile icon={Database} label="Data" value={`${(database?.data_kb || 0).toLocaleString()} KB`} sub={`storage on disk ${database?.storage_kb || 0} KB`} testid="health-db-data" />
            <StatTile icon={Database} label="Indexes" value={`${(database?.index_kb || 0).toLocaleString()} KB`} sub={(database?.total_kb || 0) > 0 ? `${Math.round((database.index_kb || 0) / database.total_kb * 100)}% of DB size` : ''} testid="health-db-indexes" />
            <StatTile
              icon={Database}
              label="DB used"
              value={`${(database?.pct_of_disk ?? 0).toFixed(4)}%`}
              sub={`${((database?.total_kb || 0) / 1024).toFixed(2)} MB of ${system?.disk_app?.total_gb || 0} GB disk`}
              tone={pctTone(database?.pct_of_disk || 0)}
              testid="health-db-total"
            />
          </div>
          <div className="border-t border-border pt-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Per-Collection</p>
            <div className="space-y-1">
              <div className="grid grid-cols-5 gap-2 text-[11px] text-muted-foreground px-2">
                <span>Collection</span><span className="text-right">Docs</span><span className="text-right">Data</span><span className="text-right">Storage</span><span className="text-right">Indexes</span>
              </div>
              {(collections || []).map((c) => (
                <div key={c.name} className="grid grid-cols-5 gap-2 text-xs px-2 py-1 hover:bg-muted/30 rounded" data-testid={`health-coll-${c.name}`}>
                  <span className="font-medium truncate">{c.name}</span>
                  <span className="tabular-nums text-right">{(c.docs || 0).toLocaleString()}</span>
                  <span className="tabular-nums text-right text-muted-foreground">{(c.size_kb || 0).toLocaleString()} KB</span>
                  <span className="tabular-nums text-right text-muted-foreground">{(c.storage_kb || 0).toLocaleString()} KB</span>
                  <span className="tabular-nums text-right text-muted-foreground">{(c.index_kb || 0).toLocaleString()} KB</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* App counts */}
      <Card data-testid="health-counts-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4 text-primary" />App Records</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <StatTile icon={Users} label="Users" value={app_counts?.users ?? '—'} sub={`${app_counts?.active_users || 0} active`} testid="health-cnt-users" />
            <StatTile icon={Users} label="Admins" value={app_counts?.admin_users ?? '—'} testid="health-cnt-admins" />
            <StatTile icon={Receipt} label="Transactions" value={(app_counts?.transactions || 0).toLocaleString()} sub={`${app_counts?.internal_transfers || 0} self-transfers`} testid="health-cnt-txns" />
            <StatTile icon={Receipt} label="Recurring" value={app_counts?.recurring_sources ?? '—'} sub="sources" testid="health-cnt-recurring" />
            <StatTile icon={Receipt} label="Statements" value={app_counts?.statements ?? '—'} testid="health-cnt-stmts" />
            <StatTile icon={Lock} label="Lockouts" value={app_counts?.login_attempts_active ?? '—'} sub="active records" tone={app_counts?.login_attempts_active > 0 ? 'warn' : 'good'} testid="health-cnt-lockouts" />
          </div>
        </CardContent>
      </Card>

      {/* Latency probes */}
      {latency && (
        <Card data-testid="health-latency-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4 text-primary" />MongoDB latency probes</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(latency.probes || []).map((p) => (
                <div key={p.op} className="p-3 rounded-md border border-border" data-testid={`health-probe-${p.op}`}>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{p.op}</p>
                  <p className={`text-xl font-bold tabular-nums font-['Outfit'] ${p.ms > 200 ? 'text-amber-600' : 'text-green-600 dark:text-green-400'}`}>{p.ms} ms</p>
                  {!p.ok && <Badge className="text-[9px] bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0 mt-1">failed</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent login records */}
      {(recent_login_records || []).length > 0 && (
        <Card data-testid="health-logins-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Lock className="h-4 w-4 text-primary" />Recent failed-login records</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {recent_login_records.map((r, i) => (
                <div key={`${r.identifier}-${i}`} className="flex items-center justify-between text-xs p-2 rounded border border-border" data-testid={`health-login-${i}`}>
                  <span className="font-mono truncate">{r.identifier}</span>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <Badge variant={r.attempts >= 5 ? 'destructive' : 'outline'} className="text-[10px]">{r.attempts} attempts</Badge>
                    {r.locked_until && <span className="text-[10px] text-amber-600 dark:text-amber-400">locked until {new Date(r.locked_until).toLocaleTimeString()}</span>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Clear stale lockouts from Admin → Users → click user → Unlock.</p>
          </CardContent>
        </Card>
      )}

      {/* Resource Usage History Charts */}
      <Card data-testid="health-metrics-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Resource Usage History
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {metricsData?.count ?? 0} samples · every 30 min · {metricsDays}-day window
            </p>
          </div>
          <div className="flex gap-1 p-1 rounded-md bg-muted/50" data-testid="metrics-range-selector">
            {[7, 14].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setMetricsDays(d)}
                data-testid={`metrics-range-${d}`}
                className={`text-[11px] px-3 py-0.5 rounded transition-colors ${metricsDays === d ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              >{d}D</button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {chartSamples.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No historical samples yet — data accumulates every 30 minutes. Check back soon.
            </p>
          ) : (
            <>
              {/* CPU Chart */}
              <MetricChart
                data={chartSamples}
                dataKey="cpu_pct"
                label="CPU Usage %"
                color="hsl(217,91%,60%)"
                testid="chart-cpu"
              />
              {/* RAM Chart */}
              <MetricChart
                data={chartSamples}
                dataKey="ram_pct"
                label="RAM Usage %"
                color="hsl(38,92%,50%)"
                testid="chart-ram"
              />
              {/* Disk /app Chart */}
              <MetricChart
                data={chartSamples}
                dataKey="disk_app_pct"
                label="Disk /app Usage %"
                color="hsl(160,60%,45%)"
                testid="chart-disk"
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricChart({ data, dataKey, label, color, testid }) {
  const latest = data.length > 0 ? data[data.length - 1][dataKey] : null;
  const tone = latest == null ? 'default' : latest >= 90 ? 'bad' : latest >= 70 ? 'warn' : 'good';
  const toneColor = tone === 'bad' ? 'text-red-500' : tone === 'warn' ? 'text-amber-600' : 'text-green-600 dark:text-green-400';

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {latest != null && (
          <span className={`text-sm font-bold tabular-nums font-['Outfit'] ${toneColor}`}>{latest}%</span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false} tickLine={false}
            interval="preserveStartEnd" minTickGap={60}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false} tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={32}
          />
          <Tooltip
            formatter={(v) => [`${v}%`, label]}
            labelFormatter={(l) => l}
            contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
            itemStyle={{ color: 'hsl(var(--card-foreground))' }}
            labelStyle={{ color: 'hsl(var(--card-foreground))' }}
          />
          <ReferenceLine y={90} stroke="hsl(0,84%,60%)" strokeDasharray="4 3" strokeWidth={1} />
          <ReferenceLine y={70} stroke="hsl(38,92%,50%)" strokeDasharray="4 3" strokeWidth={1} />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
